import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { Cell, Table, Text, useTable, type ColumnConfig, type SortDescriptor } from "@backstage/ui";
import {
	AVD_PLUGIN_ID,
	BUNDLE_INVENTORY_PATH,
	type BundleInventoryItem,
	type BundleInventoryResponse,
	type BundleInventoryType,
	type ColleagueBaseSummary,
	daysIdle,
} from "@internal/backstage-plugin-avd-common";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { descriptorFor, type BundleInventoryDescriptor } from "./bundleDescriptors";
import { deriveHealth, healthRank } from "./bundleHealth";
import { HealthCell, IdleCell, LifecycleCell, OwnerCell, ownerLabel } from "./inventoryCells";

// The page fails loud, like the AVD page it generalises: a read that failed is never allowed to look
// like an empty registry. `items` and `error` stay independent so a failed refresh keeps the rows.

/** The inventory read, as the page consumes it. */
export type BundleInventory = {
	/** The rows, once a read has succeeded. `undefined` until the first one does. */
	items?: BundleInventoryItem[];
	types: BundleInventoryType[];
	bases: ColleagueBaseSummary[];
	/** When the server last read a type live; absent when no type has a live part. */
	refreshedAt?: Date;
	error?: string;
	loading: boolean;
	refresh: () => void;
};

function readError(status: number): string {
	// RequireAdmin already caught a non-admin, so a 403 here means the two checks disagree.
	if (status === 403) {
		return "The registry refused this request (403). Your admin group membership may have changed since you signed in — reload to pick it up.";
	}
	return `The bundle registry responded ${status}.`;
}

/** Reads the whole inventory: once on mount, then once per `refresh`; never a poll (the read reconciles). */
export function useBundleInventory(): BundleInventory {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [items, setItems] = useState<BundleInventoryItem[] | undefined>(undefined);
	const [types, setTypes] = useState<BundleInventoryType[]>([]);
	const [bases, setBases] = useState<ColleagueBaseSummary[]>([]);
	const [refreshedAt, setRefreshedAt] = useState<Date | undefined>(undefined);
	const [error, setError] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [nonce, setNonce] = useState(0);

	useEffect(() => {
		let active = true;
		setLoading(true);
		const load = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${BUNDLE_INVENTORY_PATH}`);
				if (!res.ok) throw new Error(readError(res.status));
				const body = (await res.json()) as BundleInventoryResponse;
				if (!active) return;
				// A type whose source failed has no rows in this response, which says nothing about
				// whether it has any: keep its last good rows rather than erase them.
				const failed = new Set(
					(body.types ?? []).filter((t) => t.failed).map((t) => t.bundleType),
				);
				setItems((prev) => [
					...(body.items ?? []),
					...(prev ?? []).filter((i) => failed.has(i.bundleType)),
				]);
				setTypes(body.types ?? []);
				setBases(body.bases ?? []);
				const reads = (body.types ?? []).flatMap((t) => (t.readAt ? [t.readAt] : []));
				const anyRead = (body.types ?? []).some((t) => !t.failed);
				// A read where every source failed is no read: keep the last successful time, or none.
				setRefreshedAt((prev) =>
					reads.length ? new Date(reads.sort().at(-1)!) : anyRead ? new Date() : prev,
				);
				setError(undefined);
			} catch (e) {
				if (!active) return;
				setError(e instanceof Error ? e.message : "The bundle registry is unreachable.");
			} finally {
				if (active) setLoading(false);
			}
		};
		void load();
		return () => {
			active = false;
		};
	}, [discoveryApi, fetchApi, nonce]);

	const refresh = useCallback(() => setNonce((n) => n + 1), []);
	return { items, types, bases, refreshedAt, error, loading, refresh };
}

type InventoryRow = BundleInventoryItem & { id: string };

/** One bundle's identity across reads; the page and the table agree on it. */
export const bundleKey = (item: BundleInventoryItem): string =>
	[item.bundleType, item.slug, item.bundleId ?? ""].join("/");

const toRow = (item: BundleInventoryItem): InventoryRow => ({ ...item, id: bundleKey(item) });

/** The columns, in the order an operator scans them. `now` is injected so a render agrees with itself. */
export function inventoryColumns(
	now: Date,
	types: readonly BundleInventoryType[],
	descriptors: readonly BundleInventoryDescriptor[],
	options: { hideColleague?: boolean } = {},
): ColumnConfig<InventoryRow>[] {
	const descriptor = (item: BundleInventoryItem) => descriptorFor(descriptors, item.bundleType);
	const label = (type: string) => types.find((t) => t.bundleType === type)?.displayName ?? type;
	return [
		{
			id: "colleague",
			label: "Colleague",
			isRowHeader: true,
			isSortable: true,
			isHidden: options.hideColleague,
			width: "24%",
			cell: (item) => (
				<Cell>
					<OwnerCell item={item} />
					{item.owner.displayName ? (
						<Text as="div" variant="body-small" color="secondary">
							{item.owner.displayName}
						</Text>
					) : null}
				</Cell>
			),
		},
		{
			id: "bundle",
			label: "Bundle",
			isSortable: true,
			width: "18%",
			cell: (item) => (
				<Cell>
					<Text as="div">{label(item.bundleType)}</Text>
					<Text as="div" variant="body-small" color="secondary">
						{item.bundleId ?? "—"}
					</Text>
				</Cell>
			),
		},
		{
			id: "health",
			label: "Health",
			isSortable: true,
			width: "26%",
			cell: (item) => (
				<Cell>
					<HealthCell item={item} descriptor={descriptor(item)} now={now} />
				</Cell>
			),
		},
		{
			id: "lastUsed",
			label: "Last used",
			isSortable: true,
			width: "14%",
			cell: (item) => (
				<Cell>
					<IdleCell item={item} now={now} />
				</Cell>
			),
		},
		{
			id: "lifecycle",
			label: "Lifecycle",
			isSortable: true,
			width: "18%",
			cell: (item) => (
				<Cell>
					<LifecycleCell item={item} now={now} />
				</Cell>
			),
		},
	];
}

/** How each column orders rows; ties fall back to the colleague so a sort is stable. */
function sortRows(
	rows: InventoryRow[],
	sort: SortDescriptor,
	descriptors: readonly BundleInventoryDescriptor[],
	now: Date,
): InventoryRow[] {
	const idle = (i: InventoryRow) => daysIdle(i.lastSeenAt, now, i.lastSeenSource) ?? -1;
	const key: Record<string, (a: InventoryRow, b: InventoryRow) => number> = {
		colleague: (a, b) => ownerLabel(a).localeCompare(ownerLabel(b)),
		bundle: (a, b) =>
			a.bundleType.localeCompare(b.bundleType) ||
			(a.bundleId ?? "").localeCompare(b.bundleId ?? ""),
		health: (a, b) =>
			healthRank(deriveHealth(a, descriptorFor(descriptors, a.bundleType), now)) -
			healthRank(deriveHealth(b, descriptorFor(descriptors, b.bundleType), now)),
		// Longest idle first when ascending, so the bundles nearest reclamation lead.
		lastUsed: (a, b) => idle(b) - idle(a),
		lifecycle: (a, b) => a.lifecycle.localeCompare(b.lifecycle),
	};
	const compare = key[String(sort.column)];
	if (!compare) return rows;
	const sign = sort.direction === "descending" ? -1 : 1;
	return [...rows].sort(
		(a, b) => sign * compare(a, b) || ownerLabel(a).localeCompare(ownerLabel(b)),
	);
}

/** The inventory table. Presentational: the page owns the fetch and the filters. */
export function BundleInventoryTable(
	props: Readonly<{
		items: BundleInventoryItem[];
		types: readonly BundleInventoryType[];
		descriptors: readonly BundleInventoryDescriptor[];
		now?: Date;
		emptyContent?: ReactNode;
		/** A row was pressed; the page owns the detail sheet. */
		onOpen: (item: BundleInventoryItem) => void;
		/** One colleague's rows inside a group: no colleague column, no paging. */
		compact?: boolean;
	}>,
): ReactNode {
	const now = props.now ?? new Date();
	const { descriptors } = props;
	// Read through a ref so a render's fresh `now` does not re-sort every row.
	const nowRef = useRef(now);
	nowRef.current = now;
	const rows = useMemo(() => props.items.map(toRow), [props.items]);
	const { tableProps } = useTable<InventoryRow>({
		mode: "complete",
		data: rows,
		initialSort: { column: "colleague", direction: "ascending" },
		sortFn: useCallback(
			(data: InventoryRow[], sort: SortDescriptor) =>
				sortRows(data, sort, descriptors, nowRef.current),
			[descriptors],
		),
		paginationOptions: {
			type: props.compact ? "none" : "page",
			pageSize: 20,
			pageSizeOptions: [10, 20, 50],
		},
	});
	const columns = useMemo(
		() => inventoryColumns(now, props.types, descriptors, { hideColleague: props.compact }),
		[now, props.types, descriptors, props.compact],
	);
	return (
		<Table<InventoryRow>
			{...tableProps}
			columnConfig={columns}
			rowConfig={{ onClick: props.onOpen }}
			emptyState={props.emptyContent}
		/>
	);
}
